import React, { useState, useEffect, useCallback } from 'react';
import { actualizarLogisticaTramite } from '../services/tramiteService';
import DatosMaestrosService from '../services/datosMaestrosService';
import ComboboxDatosMaestros from './ComboboxDatosMaestros';
import TarjetaFechaLiquidar from './Tramite/TarjetaFechaLiquidar';
import { TarjetaFilaPlanilla } from './Tramite/FilaPlanilla';

// Carpeta física donde está guardado el papel del trámite. Componente autocontenido: se coloca
// arriba en la vista detallada, junto al nombre. Guarda contra el endpoint acotado
// /api/tramites/{id}/logistica.
//
// La carpeta salió de ser texto libre y pasó a ser un dato maestro (ver V202), así que acá se
// elige del catálogo. El combobox trabaja con nombres, no con ids: el id se resuelve contra la
// lista cargada recién al guardar, creando la carpeta en ese momento si todavía no existe (no se
// depende de que el combobox la haya creado antes por su cuenta, ver comentario en guardar()).
//
// ACÁ NO SE TOCA LA FECHA DE LIQUIDAR. Estaba metida en esta tarjeta —suelta arriba y otra vez
// adentro del modo "Editar", las dos escribiendo el mismo estado— y editar en una pisaba a la
// otra. Vive en TarjetaFechaLiquidar, al lado. Por eso guardar() tampoco manda iniciarDiferido ni
// fechaInicioEstimada: el endpoint es parcial y los campos que no viajan quedan como están.
function TarjetaCarpeta({ tramite, onUpdated }) {
    const [editando, setEditando] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');
    const [carpetas, setCarpetas] = useState([]);
    const [nombreCarpeta, setNombreCarpeta] = useState('');
    const [ubicacionCarpeta, setUbicacionCarpeta] = useState('');

    useEffect(() => {
        setNombreCarpeta(tramite?.carpetaNombre || '');
        setUbicacionCarpeta('');
    }, [tramite]);

    // El catálogo se pide al abrir la edición, no en cada render de la ficha: depende de
    // `editando` y no del trámite, que cambia de identidad en cada refresco del padre.
    const cargarCarpetas = useCallback(async () => {
        try {
            const respuesta = await DatosMaestrosService.getCarpetas(true);
            setCarpetas(respuesta.data);
        } catch (e) {
            setError('No se pudo cargar el listado de carpetas.');
        }
    }, []);

    useEffect(() => {
        if (editando) cargarCarpetas();
    }, [editando, cargarCarpetas]);

    const guardar = async () => {
        setGuardando(true);
        setError('');
        try {
            const buscado = nombreCarpeta.trim();
            const buscadoLower = buscado.toLowerCase();
            let elegida = carpetas.find(c => (c.nombre || '').trim().toLowerCase() === buscadoLower);

            if (buscado && !elegida) {
                // El combobox crea la carpeta al salir del campo, pero eso es asíncrono (tiene un
                // delay antes de llamar al backend) y confirmar puede llegar antes de que termine.
                // En vez de depender de esa carrera, si todavía no existe la creamos acá mismo.
                try {
                    const creada = await DatosMaestrosService.createCarpeta({
                        nombre: buscado,
                        ubicacion: ubicacionCarpeta.trim(),
                        descripcion: 'Creada desde la ficha del trámite',
                    });
                    elegida = creada.data;
                    setCarpetas((prev) => [...prev, elegida]);
                } catch (e) {
                    // Puede haber sido creada justo antes por el combobox (blur); si el choque es
                    // por nombre duplicado, la buscamos de nuevo en vez de fallar.
                    if (e?.response?.status === 400) {
                        const respuesta = await DatosMaestrosService.getCarpetas(true);
                        setCarpetas(respuesta.data);
                        elegida = respuesta.data.find(c => (c.nombre || '').trim().toLowerCase() === buscadoLower);
                    }
                    if (!elegida) throw e;
                }
            }

            const actualizado = await actualizarLogisticaTramite(tramite.idTramite, {
                idCarpeta: elegida ? elegida.id : null,
                // Un idCarpeta nulo no alcanza para desasignar: en los updates parciales significa
                // "el payload no trae este campo". Vaciar el campo se pide con este flag.
                limpiarCarpeta: !buscado,
            });
            setEditando(false);
            // Se mergean solo los campos de carpeta para no pisar los campos específicos del
            // tipo de trámite que trae la vista (el endpoint devuelve un TramiteDto base).
            if (onUpdated) onUpdated((prev) => ({
                ...prev,
                idCarpeta: actualizado.idCarpeta,
                carpetaNombre: actualizado.carpetaNombre,
                carpetaUbicacion: actualizado.carpetaUbicacion,
                ubicacion: actualizado.ubicacion,
            }));
        } catch (e) {
            setError(e?.response?.data?.message || 'No se pudo guardar la carpeta.');
        } finally {
            setGuardando(false);
        }
    };

    const cancelar = () => {
        setNombreCarpeta(tramite?.carpetaNombre || '');
        setUbicacionCarpeta('');
        setError('');
        setEditando(false);
    };

    // Una carpeta recién creada desde el combobox tiene que entrar en la lista local, si no el
    // guardado no encuentra su id.
    const agregarCarpetaCreada = (creada) => {
        setCarpetas((prev) => [...prev, creada]);
    };

    const tarjeta = {
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
        padding: '8px 12px', fontSize: '0.9rem',
        flex: '1 1 260px', minWidth: 260,
    };

    if (!editando) {
        return (
            <div style={tarjeta}>
                {error && (
                    <div className="alert alert-danger py-1 px-2 mb-0" style={{ fontSize: '0.82rem', width: '100%' }}>
                        {error}
                    </div>
                )}
                <span style={{ color: '#334155' }}>
                    <i className="bi bi-folder-fill me-1" style={{ color: '#0d9488' }} />
                    <strong>Carpeta:</strong>{' '}
                    {tramite.carpetaNombre
                        ? (
                            <span>
                                {tramite.carpetaNombre}
                                {tramite.carpetaUbicacion && (
                                    <span style={{ color: '#64748b' }}> · {tramite.carpetaUbicacion}</span>
                                )}
                            </span>
                        )
                        : <span style={{ color: '#94a3b8' }}>sin asignar</span>}
                </span>
                <button
                    className="btn btn-sm btn-outline-secondary"
                    style={{ marginLeft: 'auto', padding: '2px 10px', fontSize: '0.8rem' }}
                    onClick={() => setEditando(true)}
                >
                    <i className="bi bi-pencil me-1" />Editar
                </button>
            </div>
        );
    }

    // Si el nombre tipeado coincide con una carpeta del catálogo, esa es la que se va a asignar
    // (su ubicación es la del ABM, no se edita desde acá). Si no coincide con ninguna, es una
    // carpeta nueva y se puede indicar dónde va a quedar físicamente al crearla.
    const buscadoActual = nombreCarpeta.trim().toLowerCase();
    const carpetaCoincidente = buscadoActual
        ? carpetas.find(c => (c.nombre || '').trim().toLowerCase() === buscadoActual)
        : null;
    const esCarpetaNueva = !!buscadoActual && !carpetaCoincidente;

    return (
        <div style={{ ...tarjeta, flexDirection: 'column', alignItems: 'stretch' }}>
            {error && (
                <div className="alert alert-danger py-1 px-2 mb-0" style={{ fontSize: '0.82rem' }}>
                    {error}
                </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 8 }}>
                <label style={{ fontWeight: 600, color: '#334155', margin: '6px 0 0' }}>Carpeta:</label>
                <div style={{ minWidth: 260, flex: '0 1 320px' }}>
                    <ComboboxDatosMaestros
                        opciones={carpetas}
                        valor={nombreCarpeta}
                        onChange={setNombreCarpeta}
                        tipo="carpeta"
                        placeholder="Elegí una carpeta o escribí una nueva"
                        onCrearNuevo={agregarCarpetaCreada}
                        datosCreacionExtra={{ ubicacion: ubicacionCarpeta }}
                    />
                </div>
                {nombreCarpeta && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ marginTop: 2, fontSize: '0.8rem' }}
                        onClick={() => { setNombreCarpeta(''); setUbicacionCarpeta(''); }}
                        title="Dejar el trámite sin carpeta"
                    >
                        Sin asignar
                    </button>
                )}
            </div>
            {carpetaCoincidente && (
                <div style={{ color: '#64748b', fontSize: '0.82rem', marginLeft: 2 }}>
                    <i className="bi bi-geo-alt me-1" />
                    Ubicación: {carpetaCoincidente.ubicacion || 'sin definir'}
                </div>
            )}
            {esCarpetaNueva && (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                    <label style={{ color: '#334155', margin: 0, fontSize: '0.85rem' }}>Ubicación física:</label>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ maxWidth: 240 }}
                        placeholder="Ej: Estante 5, fila B (opcional)"
                        value={ubicacionCarpeta}
                        onChange={(e) => setUbicacionCarpeta(e.target.value)}
                    />
                    <small className="text-muted">Es una carpeta nueva; se guarda con esta ubicación al confirmar.</small>
                </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-sm btn-primary" onClick={guardar} disabled={guardando} style={{ fontWeight: 600 }}>
                    {guardando ? 'Guardando…' : 'Guardar'}
                </button>
                <button className="btn btn-sm btn-outline-secondary" onClick={cancelar} disabled={guardando}>
                    Cancelar
                </button>
            </div>
        </div>
    );
}

/**
 * La franja de logística de la ficha del trámite: dónde está el papel y cuándo se liquida.
 *
 * Son dos tarjetas independientes, una al lado de la otra, cada una con su propio estado y su
 * propio guardado. Antes eran una sola y la fecha de liquidar tenía dos editores compitiendo por
 * el mismo estado (ver el comentario de TarjetaCarpeta).
 *
 * Sigue siendo un único componente para los 13 lugares que lo montan: las vistas detalladas de
 * cada módulo lo usan igual que antes, con las mismas props.
 *
 * Props:
 *  - tramite:  { idTramite, carpetaNombre, carpetaUbicacion, iniciarDiferido, fechaInicioEstimada }
 *  - onUpdated: (actualizador) => void   (opcional, para refrescar la vista)
 */
export default function PanelUbicacionTramite({ tramite, onUpdated }) {
    if (!tramite?.idTramite) return null;

    return (
        <div style={{ margin: '8px 0' }}>
            {/* Carpeta y fecha de liquidar en la misma fila; si no entran, cada una baja a lo largo. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 8 }}>
                <TarjetaCarpeta tramite={tramite} onUpdated={onUpdated} />
                <TarjetaFechaLiquidar tramite={tramite} onUpdated={onUpdated} />
            </div>
            {/* Constancia de origen de la migración del Excel, en su propia línea: solo aparece si
                el trámite vino de ahí. */}
            <div style={{ display: 'flex', marginTop: 8 }}>
                <TarjetaFilaPlanilla idTramite={tramite.idTramite} />
            </div>
        </div>
    );
}
